<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import Modal from '@/client/components/common/modal.vue';
import CopyButton from '@/client/components/common/CopyButton.vue';
import FundingService from '@/client/service/funding';
import type { ProviderConfig, PayPalCredentials, StripeCredentials } from '@/client/service/funding';

const { t } = useTranslation('admin', {
  keyPrefix: 'funding.wizard',
});

// Service instance
const fundingService = new FundingService();

// Props
const props = defineProps<{
  show: boolean;
  unconfiguredProviders: ProviderConfig[];
}>();

// Emits
const emit = defineEmits<{
  close: [];
  'provider-connected': [];
}>();

// State
const currentStep = ref(1);
const selectedProvider = ref<string | null>(null);
const error = ref<string | null>(null);
const connecting = ref(false);
const connectionVerified = ref(false);

// PayPal form state
const paypalClientId = ref('');
const paypalClientSecret = ref('');
const paypalEnvironment = ref<'sandbox' | 'production'>('sandbox');
const paypalErrors = ref({
  clientId: '',
  clientSecret: '',
  environment: '',
});

// Stripe form state
const stripePublishableKey = ref('');
const stripeSecretKey = ref('');
const stripeWebhookSecret = ref('');
const stripeErrors = ref({
  publishableKey: '',
  secretKey: '',
  webhookSecret: '',
});

// Computed
const totalSteps = computed(() => 3);

const selectedProviderConfig = computed(() => {
  return props.unconfiguredProviders.find(p => p.provider_type === selectedProvider.value);
});

const selectedProviderName = computed(() => {
  if (selectedProvider.value === 'stripe') {
    return t('providers.stripe_name');
  }
  else if (selectedProvider.value === 'paypal') {
    return t('providers.paypal_name');
  }
  return selectedProvider.value || '';
});

const isPayPalFormValid = computed(() => {
  return paypalClientId.value.trim() &&
         paypalClientSecret.value.trim() &&
         paypalEnvironment.value &&
         !paypalErrors.value.clientId &&
         !paypalErrors.value.clientSecret &&
         !paypalErrors.value.environment;
});

const isStripeFormValid = computed(() => {
  return stripePublishableKey.value.trim() &&
         stripeSecretKey.value.trim() &&
         stripeWebhookSecret.value.trim() &&
         !stripeErrors.value.publishableKey &&
         !stripeErrors.value.secretKey &&
         !stripeErrors.value.webhookSecret;
});

/**
 * Select a provider on step 1
 */
function selectProvider(providerType: string) {
  selectedProvider.value = providerType;
  error.value = null;
}

/**
 * Navigate to next step
 */
function goToNextStep() {
  if (currentStep.value === 1 && selectedProvider.value) {
    currentStep.value = 2;
    error.value = null;
  }
}

/**
 * Navigate to previous step
 */
function goToPreviousStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    error.value = null;
  }
}

/**
 * Close wizard and reset state
 */
function closeWizard() {
  resetWizard();
  emit('close');
}

/**
 * Reset wizard to initial state
 */
function resetWizard() {
  currentStep.value = 1;
  selectedProvider.value = null;
  error.value = null;
  connecting.value = false;
  connectionVerified.value = false;
  resetPayPalForm();
  resetStripeForm();
}

/**
 * Reset PayPal form fields
 */
function resetPayPalForm() {
  paypalClientId.value = '';
  paypalClientSecret.value = '';
  paypalEnvironment.value = 'sandbox';
  paypalErrors.value = {
    clientId: '',
    clientSecret: '',
    environment: '',
  };
}

/**
 * Reset Stripe form fields
 */
function resetStripeForm() {
  stripePublishableKey.value = '';
  stripeSecretKey.value = '';
  stripeWebhookSecret.value = '';
  stripeErrors.value = {
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  };
}

/**
 * Validate PayPal form field on blur
 */
function validatePayPalField(field: 'clientId' | 'clientSecret' | 'environment') {
  if (field === 'clientId') {
    paypalErrors.value.clientId = paypalClientId.value.trim() ? '' : t('step2.paypal_client_id_required');
  }
  else if (field === 'clientSecret') {
    paypalErrors.value.clientSecret = paypalClientSecret.value.trim() ? '' : t('step2.paypal_client_secret_required');
  }
  else if (field === 'environment') {
    paypalErrors.value.environment = paypalEnvironment.value ? '' : t('step2.paypal_environment_required');
  }
}

/**
 * Validate Stripe form field on blur
 */
function validateStripeField(field: 'publishableKey' | 'secretKey' | 'webhookSecret') {
  const value = field === 'publishableKey'
    ? stripePublishableKey.value.trim()
    : field === 'secretKey'
      ? stripeSecretKey.value.trim()
      : stripeWebhookSecret.value.trim();

  if (!value) {
    if (field === 'publishableKey') {
      stripeErrors.value.publishableKey = t('step2.stripe_publishable_key_required');
    }
    else if (field === 'secretKey') {
      stripeErrors.value.secretKey = t('step2.stripe_secret_key_required');
    }
    else if (field === 'webhookSecret') {
      stripeErrors.value.webhookSecret = t('step2.stripe_webhook_secret_required');
    }
    return;
  }

  // Format validation
  if (field === 'publishableKey' && !value.startsWith('pk_test_') && !value.startsWith('pk_live_')) {
    stripeErrors.value.publishableKey = t('step2.stripe_publishable_key_format');
  }
  else if (field === 'publishableKey') {
    stripeErrors.value.publishableKey = '';
  }

  if (field === 'secretKey' && !value.startsWith('sk_test_') && !value.startsWith('sk_live_')) {
    stripeErrors.value.secretKey = t('step2.stripe_secret_key_format');
  }
  else if (field === 'secretKey') {
    stripeErrors.value.secretKey = '';
  }

  if (field === 'webhookSecret' && !value.startsWith('whsec_')) {
    stripeErrors.value.webhookSecret = t('step2.stripe_webhook_secret_format');
  }
  else if (field === 'webhookSecret') {
    stripeErrors.value.webhookSecret = '';
  }
}

/**
 * Handle PayPal configuration submission
 */
async function configurePayPal() {
  // Validate form
  if (!isPayPalFormValid.value) {
    validatePayPalField('clientId');
    validatePayPalField('clientSecret');
    validatePayPalField('environment');
    return;
  }

  try {
    connecting.value = true;
    error.value = null;

    const credentials: PayPalCredentials = {
      client_id: paypalClientId.value.trim(),
      client_secret: paypalClientSecret.value.trim(),
      environment: paypalEnvironment.value,
    };

    const success = await fundingService.configurePayPal(credentials);

    if (success) {
      // Move to success step
      connectionVerified.value = true;
      currentStep.value = 3;
      connecting.value = false;
    }
    else {
      error.value = t('errors.connection_failed', { provider: selectedProviderName.value });
      connecting.value = false;
    }
  }
  catch (err) {
    console.error('Failed to configure PayPal:', err);
    error.value = t('errors.connection_failed', { provider: selectedProviderName.value });
    connecting.value = false;
  }
}

/**
 * Handle Stripe configuration submission
 */
async function configureStripe() {
  // Validate form
  if (!isStripeFormValid.value) {
    validateStripeField('publishableKey');
    validateStripeField('secretKey');
    validateStripeField('webhookSecret');
    return;
  }

  try {
    connecting.value = true;
    error.value = null;

    const credentials: StripeCredentials = {
      publishable_key: stripePublishableKey.value.trim(),
      secret_key: stripeSecretKey.value.trim(),
      webhook_secret: stripeWebhookSecret.value.trim(),
    };

    const result = await fundingService.configureStripe(credentials);

    if (result.success) {
      connectionVerified.value = result.connectionVerified;
      currentStep.value = 3;
      connecting.value = false;
    }
    else {
      error.value = t('errors.connection_failed', { provider: selectedProviderName.value });
      connecting.value = false;
    }
  }
  catch (err) {
    console.error('Failed to configure Stripe:', err);
    error.value = t('errors.connection_failed', { provider: selectedProviderName.value });
    connecting.value = false;
  }
}

/**
 * Handle Done button on success step
 */
function handleSuccess() {
  emit('provider-connected');
  emit('close');
  resetWizard();
}
</script>

<template>
  <Modal
    v-if="show"
    :title="t('title')"
    :initiallyOpen="true"
    @close="closeWizard"
  >
    <!-- Progress Indicator -->
    <div class="progress-indicator">
      {{ t('step_indicator', { current: currentStep, total: totalSteps }) }}
    </div>

    <!-- Wizard Content -->
    <div class="wizard-content">
      <!-- Error Message -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Step 1: Provider Selection -->
      <div v-if="currentStep === 1" class="step-content">
        <h3 class="step-title">{{ t('step1.title') }}</h3>
        <p class="step-description">{{ t('step1.description') }}</p>

        <div class="provider-grid">
          <div
            v-for="provider in unconfiguredProviders"
            :key="provider.provider_type"
            class="provider-card"
            :class="{ selected: selectedProvider === provider.provider_type }"
            @click="selectProvider(provider.provider_type)"
          >
            <div class="provider-icon">
              {{ provider.provider_type === 'stripe' ? '&#x1F4B3;' : '&#x1F17F;&#xFE0F;' }}
            </div>
            <h4 class="provider-name">
              {{ t(`providers.${provider.provider_type}_name`) }}
            </h4>
            <p class="provider-description">
              {{ t(`providers.${provider.provider_type}_description`) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Step 2: Provider Configuration -->
      <div v-if="currentStep === 2" class="step-content">
        <!-- Stripe Configuration -->
        <div v-if="selectedProvider === 'stripe'" class="provider-config">
          <h3 class="step-title">{{ t('step2.stripe_title') }}</h3>
          <p class="step-description">{{ t('step2.stripe_description') }}</p>

          <form @submit.prevent="configureStripe">
            <div class="form-group">
              <label for="stripe-publishable-key" class="form-label">
                {{ t('step2.stripe_publishable_key_label') }}
                <span class="required">*</span>
              </label>
              <input
                id="stripe-publishable-key"
                v-model="stripePublishableKey"
                type="text"
                class="form-input"
                :class="{ 'has-error': stripeErrors.publishableKey }"
                :placeholder="t('step2.stripe_publishable_key_placeholder')"
                :disabled="connecting"
                :aria-describedby="stripeErrors.publishableKey ? 'stripe-pk-error' : 'stripe-pk-help'"
                @blur="validateStripeField('publishableKey')"
              />
              <div v-if="stripeErrors.publishableKey" id="stripe-pk-error" class="error-message-inline">
                {{ stripeErrors.publishableKey }}
              </div>
              <div v-else id="stripe-pk-help" class="form-help">
                {{ t('step2.stripe_publishable_key_help') }}
              </div>
            </div>

            <div class="form-group">
              <label for="stripe-secret-key" class="form-label">
                {{ t('step2.stripe_secret_key_label') }}
                <span class="required">*</span>
              </label>
              <input
                id="stripe-secret-key"
                v-model="stripeSecretKey"
                type="password"
                class="form-input"
                :class="{ 'has-error': stripeErrors.secretKey }"
                :placeholder="t('step2.stripe_secret_key_placeholder')"
                :disabled="connecting"
                :aria-describedby="stripeErrors.secretKey ? 'stripe-sk-error' : 'stripe-sk-help'"
                @blur="validateStripeField('secretKey')"
              />
              <div v-if="stripeErrors.secretKey" id="stripe-sk-error" class="error-message-inline">
                {{ stripeErrors.secretKey }}
              </div>
              <div v-else id="stripe-sk-help" class="form-help">
                {{ t('step2.stripe_secret_key_help') }}
              </div>
            </div>

            <div class="form-group">
              <label for="stripe-webhook-secret" class="form-label">
                {{ t('step2.stripe_webhook_secret_label') }}
                <span class="required">*</span>
              </label>
              <input
                id="stripe-webhook-secret"
                v-model="stripeWebhookSecret"
                type="password"
                class="form-input"
                :class="{ 'has-error': stripeErrors.webhookSecret }"
                :placeholder="t('step2.stripe_webhook_secret_placeholder')"
                :disabled="connecting"
                :aria-describedby="stripeErrors.webhookSecret ? 'stripe-wh-error' : 'stripe-wh-help'"
                @blur="validateStripeField('webhookSecret')"
              />
              <div v-if="stripeErrors.webhookSecret" id="stripe-wh-error" class="error-message-inline">
                {{ stripeErrors.webhookSecret }}
              </div>
              <div v-else id="stripe-wh-help" class="form-help">
                {{ t('step2.stripe_webhook_secret_help') }}
                <div v-if="selectedProviderConfig?.webhook_url" class="webhook-url-hint">
                  <span class="webhook-url-label">{{ t('step2.stripe_webhook_url_label') }}</span>
                  <code class="webhook-url">{{ selectedProviderConfig.webhook_url }}</code>
                  <CopyButton
                    :text="selectedProviderConfig.webhook_url"
                    :aria-label="t('step2.stripe_webhook_url_copy')"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              class="btn btn--primary connect-button"
              :disabled="!isStripeFormValid || connecting"
            >
              {{ connecting ? t('step2.connecting_button') : t('step2.stripe_configure_button') }}
            </button>
          </form>
        </div>

        <!-- PayPal Configuration -->
        <div v-if="selectedProvider === 'paypal'" class="provider-config">
          <h3 class="step-title">{{ t('step2.paypal_title') }}</h3>
          <p class="step-description">{{ t('step2.paypal_description') }}</p>

          <form @submit.prevent="configurePayPal">
            <div class="form-group">
              <label for="paypal-client-id" class="form-label">
                {{ t('step2.paypal_client_id_label') }}
                <span class="required">*</span>
              </label>
              <input
                id="paypal-client-id"
                v-model="paypalClientId"
                type="text"
                class="form-input"
                :class="{ 'has-error': paypalErrors.clientId }"
                :placeholder="t('step2.paypal_client_id_placeholder')"
                :disabled="connecting"
                :aria-describedby="paypalErrors.clientId ? 'paypal-id-error' : 'paypal-id-help'"
                @blur="validatePayPalField('clientId')"
              />
              <div v-if="paypalErrors.clientId" id="paypal-id-error" class="error-message-inline">
                {{ paypalErrors.clientId }}
              </div>
              <div v-else id="paypal-id-help" class="form-help">
                {{ t('step2.paypal_client_id_help') }}
              </div>
            </div>

            <div class="form-group">
              <label for="paypal-client-secret" class="form-label">
                {{ t('step2.paypal_client_secret_label') }}
                <span class="required">*</span>
              </label>
              <input
                id="paypal-client-secret"
                v-model="paypalClientSecret"
                type="password"
                class="form-input"
                :class="{ 'has-error': paypalErrors.clientSecret }"
                :placeholder="t('step2.paypal_client_secret_placeholder')"
                :disabled="connecting"
                :aria-describedby="paypalErrors.clientSecret ? 'paypal-secret-error' : 'paypal-secret-help'"
                @blur="validatePayPalField('clientSecret')"
              />
              <div v-if="paypalErrors.clientSecret" id="paypal-secret-error" class="error-message-inline">
                {{ paypalErrors.clientSecret }}
              </div>
              <div v-else id="paypal-secret-help" class="form-help">
                {{ t('step2.paypal_client_secret_help') }}
              </div>
            </div>

            <div class="form-group">
              <label for="paypal-environment" class="form-label">
                {{ t('step2.paypal_environment_label') }}
                <span class="required">*</span>
              </label>
              <select
                id="paypal-environment"
                v-model="paypalEnvironment"
                class="form-input"
                :class="{ 'has-error': paypalErrors.environment }"
                :disabled="connecting"
                :aria-describedby="paypalErrors.environment ? 'paypal-env-error' : 'paypal-env-help'"
                @blur="validatePayPalField('environment')"
              >
                <option value="sandbox">{{ t('step2.paypal_environment_sandbox') }}</option>
                <option value="production">{{ t('step2.paypal_environment_production') }}</option>
              </select>
              <div v-if="paypalErrors.environment" id="paypal-env-error" class="error-message-inline">
                {{ paypalErrors.environment }}
              </div>
              <div v-else id="paypal-env-help" class="form-help">
                {{ t('step2.paypal_environment_help') }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn--primary connect-button"
              :disabled="!isPayPalFormValid || connecting"
            >
              {{ connecting ? t('step2.connecting_button') : t('step2.paypal_configure_button') }}
            </button>
          </form>
        </div>
      </div>

      <!-- Step 3: Success or Warning -->
      <div v-if="currentStep === 3" class="step-content">
        <!-- Verified: Success -->
        <div v-if="connectionVerified" class="success-content">
          <div class="success-icon">&#x2713;</div>
          <h3 class="step-title">{{ t('step3.title') }}</h3>
          <p class="success-message">
            {{ t('step3.success_message', { provider: selectedProviderName }) }}
          </p>
          <p class="ready-message">{{ t('step3.ready_message') }}</p>
        </div>

        <!-- Not verified: Warning -->
        <div v-else class="warning-content">
          <div class="warning-icon">!</div>
          <h3 class="step-title">{{ t('step3.connection_warning_title') }}</h3>
          <p class="warning-message">
            {{ t('step3.connection_warning_message', { provider: selectedProviderName }) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Modal Actions -->
    <div class="modal-actions">
      <!-- Step 1 Actions -->
      <template v-if="currentStep === 1">
        <button
          type="button"
          class="btn btn--secondary cancel-button"
          @click="closeWizard"
          :disabled="connecting"
        >
          {{ t('cancel_button') }}
        </button>
        <button
          type="button"
          class="btn btn--primary continue-button"
          @click="goToNextStep"
          :disabled="!selectedProvider || connecting"
        >
          {{ t('continue_button') }}
        </button>
      </template>

      <!-- Step 2 Actions -->
      <template v-if="currentStep === 2">
        <button
          type="button"
          class="btn btn--secondary back-button"
          @click="goToPreviousStep"
          :disabled="connecting"
        >
          {{ t('back_button') }}
        </button>
        <button
          type="button"
          class="btn btn--secondary cancel-button"
          @click="closeWizard"
          :disabled="connecting"
        >
          {{ t('cancel_button') }}
        </button>
      </template>

      <!-- Step 3 Actions -->
      <template v-if="currentStep === 3">
        <button
          v-if="connectionVerified"
          type="button"
          class="btn btn--primary done-button"
          @click="handleSuccess"
        >
          {{ t('done_button') }}
        </button>
        <button
          v-else
          type="button"
          class="btn btn--secondary close-button"
          @click="closeWizard"
        >
          {{ t('step3.close_button') }}
        </button>
      </template>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
@use '../../assets/style/components/calendar-admin' as *;

.progress-indicator {
  text-align: center;
  padding: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: var(--pav-font-weight-medium);
  color: var(--pav-color-text-secondary);
}

.wizard-content {

  .error-message {
    margin-bottom: 1.5rem;
    padding: 0.75rem;
    background-color: #fff0f0;
    border: 1px solid #d87373;
    color: #7d2a2a;
    border-radius: 4px;

    @media (prefers-color-scheme: dark) {
      background-color: #4a2020;
      border-color: #8a4040;
      color: #f8b4b4;
    }
  }

  .step-content {
    .step-title {
      margin: 0 0 0.5rem 0;
      font-size: 1.25rem;
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
    }

    .step-description {
      margin: 0 0 1.5rem 0;
      color: var(--pav-color-text-secondary);
      line-height: 1.5;
    }
  }
}

// Provider Selection Grid
.provider-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;

  .provider-card {
    padding: 1.5rem;
    border: 2px solid var(--pav-color-border-primary);
    border-radius: 8px;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s ease;
    background: var(--pav-color-surface-secondary);

    &:hover {
      border-color: #4a90e2;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &.selected {
      border-color: #4a90e2;
      background: rgba(74, 144, 226, 0.05);
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
    }

    @media (prefers-color-scheme: dark) {
      background: rgba(255, 255, 255, 0.05);

      &:hover {
        border-color: #60a5fa;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      }

      &.selected {
        border-color: #60a5fa;
        background: rgba(96, 165, 250, 0.1);
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
      }
    }

    .provider-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
    }

    .provider-name {
      margin: 0 0 0.5rem 0;
      font-size: 1.125rem;
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
    }

    .provider-description {
      margin: 0;
      font-size: 0.875rem;
      color: var(--pav-color-text-secondary);
    }
  }
}

// Provider Configuration Forms
.provider-config {
  .connect-button {
    width: 100%;
    margin-top: 1rem;
  }

  .form-group {
    margin-bottom: 1.5rem;

    .form-label {
      display: block;
      font-weight: var(--pav-font-weight-medium);
      margin-bottom: 0.5rem;
      color: var(--pav-color-text-primary);

      .required {
        color: #dc3545;
        margin-left: 0.25rem;
      }
    }

    .form-input {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid var(--pav-color-border-primary);
      border-radius: 8px;
      background: var(--pav-color-surface-secondary);
      color: var(--pav-color-text-primary);
      font-size: 1rem;

      &:focus {
        outline: none;
        border-color: #4a90e2;
        box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
      }

      &.has-error {
        border-color: #dc3545;

        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: var(--pav-color-surface-hover);
      }

      @media (prefers-color-scheme: dark) {
        background: var(--pav-color-surface-tertiary);
      }
    }

    .error-message-inline {
      margin-top: 0.5rem;
      font-size: 0.875rem;
      color: #dc3545;
    }

    .form-help {
      margin-top: 0.375rem;
      font-size: 0.8125rem;
      line-height: 1.4;
      color: var(--pav-color-text-secondary);

      .webhook-url-hint {
        margin-top: 0.375rem;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.375rem;

        .webhook-url-label {
          font-weight: var(--pav-font-weight-medium);
        }

        .webhook-url {
          padding: 0.125rem 0.375rem;
          background: var(--pav-color-surface-tertiary, rgba(0, 0, 0, 0.05));
          border: 1px solid var(--pav-color-border-primary);
          border-radius: 4px;
          font-size: 0.75rem;
          user-select: all;
          word-break: break-all;
        }
      }
    }
  }
}

// Success Step
.success-content {
  text-align: center;
  padding: 2rem 0;

  .success-icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1.5rem;
    background: #d4edda;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: #155724;

    @media (prefers-color-scheme: dark) {
      background: #1e4620;
      color: #7fd68a;
    }
  }

  .success-message {
    margin-bottom: 1rem;
    font-size: 1.125rem;
    font-weight: var(--pav-font-weight-medium);
    color: var(--pav-color-text-primary);
  }

  .ready-message {
    margin: 0;
    color: var(--pav-color-text-secondary);
  }
}

// Warning Step
.warning-content {
  text-align: center;
  padding: 2rem 0;

  .warning-icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1.5rem;
    background: #fff3cd;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: bold;
    color: #856404;

    @media (prefers-color-scheme: dark) {
      background: #4a3c00;
      color: #ffd95c;
    }
  }

  .warning-message {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--pav-color-text-secondary);
  }
}

// Modal Actions
.modal-actions {
  @include modal-actions;

  button {
    min-width: 100px;
  }
}
</style>
